import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { NATIVE_OAUTH_CALLBACK } from "@/lib/oauth";
import { Loader2 } from "lucide-react";

/**
 * Public OAuth return route.
 *
 * Two jobs:
 *  - Native wrappers: the provider redirects here (a real HTTPS route the
 *    provider and Supabase accept), and this page immediately bounces to the
 *    app's custom scheme so the app can finish the PKCE exchange with the
 *    verifier it still holds. No token is ever rendered or logged.
 *  - Web: completes the code exchange (or lets the client's own URL detection
 *    do it) and sends the user to the page they meant to reach.
 */
export const Route = createFileRoute("/auth/callback")({
  ssr: false,
  component: AuthCallback,
  head: () => ({
    meta: [
      { title: "Signing you in - Inquira" },
      { name: "description", content: "Completing your Inquira sign-in securely." },
      { property: "og:title", content: "Signing you in - Inquira" },
      { property: "og:description", content: "Completing your Inquira sign-in securely." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function safeRedirectPath(value: string | null): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/";
  return value;
}

function AuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);

    // Hand control back to the native app shell.
    if (url.searchParams.get("native") === "1") {
      const params = new URLSearchParams(url.search);
      params.delete("native");
      window.location.replace(`${NATIVE_OAUTH_CALLBACK}?${params.toString()}${url.hash}`);
      return;
    }

    let cancelled = false;
    (async () => {
      const code = url.searchParams.get("code");
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          // Detailed reason stays in the console for admin diagnostics only.
          console.warn("[auth-callback] exchange failed", error.message);
        }
      }
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!data.session) {
        setFailed(true);
        return;
      }
      // The signup conversion pixel is centralized in useAuth: the SIGNED_IN
      // event from this exchange triggers recordConfirmedRegistration, which
      // fires it only for the one server-confirmed new registration.
      void navigate({ to: safeRedirectPath(url.searchParams.get("next")), replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 text-center">
      {failed ? (
        <div className="max-w-sm space-y-4">
          <p className="text-sm text-muted-foreground">{t("auth.callbackFailed")}</p>
          <button
            type="button"
            onClick={() => navigate({ to: "/login", replace: true })}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            {t("auth.backToSignIn")}
          </button>
        </div>
      ) : (
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label={t("auth.signingIn")} />
      )}
    </div>
  );
}
